import { useEffect, useRef, useState } from "react";

const plans = [
  {
    icon: "ri-heart-pulse-line",
    title: "Individual & Family Plans",
    tag: "Most Popular",
    tagColor: "bg-amber-500",
    description:
      "Comprehensive ACA-compliant plans designed to protect your entire family. Choose from HMO, PPO, and EPO options with extensive provider networks across Texas.",
    features: [
      "Preventive care at no extra cost",
      "Prescription drug coverage",
      "Mental health & wellness benefits",
      "Maternity and newborn care",
      "Pediatric dental & vision",
    ],
    image:
      "https://public.readdy.ai/ai/img_res/ec1b5fb78830af20f53c0558b1d7464e.jpg",
  },
  {
    icon: "ri-parent-line",
    title: "Medicare Solutions",
    tag: "Seniors 65+",
    tagColor: "bg-emerald-600",
    description:
      "Navigate Medicare with confidence. We offer Medicare Advantage, Medicare Supplement (Medigap), and Part D prescription plans from leading national carriers.",
    features: [
      "Medicare Advantage (Part C) plans",
      "Medigap supplement coverage",
      "Part D prescription drug plans",
      "Dental, vision & hearing included",
      "Annual wellness visits covered",
    ],
    image:
      "https://readdy.ai/api/search-image?query=happy%20senior%20couple%20in%20their%2060s%20walking%20together%20in%20a%20bright%20park%2C%20healthy%20active%20lifestyle%2C%20warm%20natural%20light%2C%20green%20environment%2C%20smiling%20elderly%20man%20and%20woman%2C%20professional%20photography%2C%20clean%20background%20with%20trees%20and%20sunshine%2C%20retirement%20lifestyle&width=600&height=360&seq=plan002&orientation=landscape",
  },
  {
    icon: "ri-shield-check-line",
    title: "Supplemental Coverage",
    tag: "Extra Protection",
    tagColor: "bg-teal-600",
    description:
      "Fill the gaps in your existing coverage with dental, vision, critical illness, accident, and short-term disability insurance plans.",
    features: [
      "Dental & orthodontic coverage",
      "Vision care & eyewear benefits",
      "Critical illness lump-sum benefits",
      "Accident & disability insurance",
      "Short-term health plans",
    ],
    image:
      "https://readdy.ai/api/search-image?query=professional%20healthcare%20worker%20consulting%20with%20patient%20in%20bright%20modern%20clinic%2C%20friendly%20doctor%20reviewing%20documents%20with%20a%20client%2C%20clean%20white%20medical%20office%2C%20warm%20natural%20light%2C%20trust%20and%20care%20concept%2C%20high%20quality%20professional%20photography%2C%20minimalist%20medical%20interior&width=600&height=360&seq=plan003&orientation=landscape",
  },
  {
    icon: "ri-briefcase-line",
    title: "Small Business Plans",
    tag: "Group Coverage",
    tagColor: "bg-amber-600",
    description:
      "Attract and retain top talent with competitive group health insurance. We offer flexible group plans for businesses of all sizes in the Irving, TX area.",
    features: [
      "Group health insurance plans",
      "Employer contribution options",
      "Dental & vision for employees",
      "Life & disability group coverage",
      "COBRA administration support",
    ],
    image:
      "https://readdy.ai/api/search-image?query=diverse%20group%20of%20small%20business%20professionals%20meeting%20in%20modern%20bright%20office%2C%20collaborative%20team%20around%20table%20with%20documents%2C%20friendly%20professional%20environment%2C%20natural%20light%20through%20large%20windows%2C%20clean%20contemporary%20workspace%2C%20business%20meeting%20photography%2C%20warm%20tones&width=600&height=360&seq=plan004&orientation=landscape",
  },
];

export default function PlansSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleScrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="plans" className="py-24 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-['Open_Sans'] font-600 tracking-widest uppercase mb-4">
              Insurance Plans
            </span>
            <h2 className="font-['Poppins'] font-700 text-4xl lg:text-5xl text-emerald-900 leading-tight">
              Find Your<br />
              <strong className="text-amber-500">Perfect Plan</strong>
            </h2>
          </div>
          <p className="font-['Open_Sans'] text-gray-500 text-base max-w-sm leading-relaxed lg:text-right">
            We work with 50+ top-rated carriers to give you the widest selection of 
            <strong> private health insurance</strong> options at the best rates in Texas.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {plans.map((plan, i) => (
            <div
              key={plan.title}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className={`bg-white border rounded-lg overflow-hidden transition-all duration-500 cursor-pointer ${
                hovered === i ? "border-emerald-400 -translate-y-1" : "border-emerald-100"
              } ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={plan.image}
                  alt={plan.title}
                  title={`${plan.title} - HealthMarkets Insurance Irving TX`}
                  className="w-full h-full object-cover object-top transition-transform duration-500"
                  style={{ transform: hovered === i ? "scale(1.03)" : "scale(1)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 border border-white/40 flex items-center justify-center">
                    <i className={`${plan.icon} text-white text-lg`}></i>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-white text-xs font-['Poppins'] font-600 ${plan.tagColor}`}>
                    {plan.tag}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-['Poppins'] font-700 text-xl text-emerald-900 mb-3">{plan.title}</h3>
                <p className="font-['Open_Sans'] text-gray-500 text-sm leading-relaxed mb-5">{plan.description}</p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-3 text-sm font-['Open_Sans'] text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-checkbox-circle-fill text-emerald-600 text-base"></i>
                      </div>
                      {feat}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={handleScrollToContact}
                  className="w-full py-3 rounded-lg border-2 border-emerald-700 text-emerald-700 font-['Poppins'] font-600 text-sm hover:bg-emerald-700 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get a Free Quote
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-12 bg-emerald-50 border border-emerald-200 rounded-lg p-8 flex flex-col lg:flex-row items-center justify-between gap-6 transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div>
            <h4 className="font-['Poppins'] font-700 text-lg text-emerald-900 mb-1">Not sure which plan is right for you?</h4>
            <p className="font-['Open_Sans'] text-gray-500 text-sm">Our licensed agents will walk you through every option — completely free, no obligation.</p>
          </div>
          <div className="flex gap-4 flex-shrink-0">
            <a href="tel:9722174662" className="px-6 py-3 bg-emerald-700 text-white font-['Poppins'] font-600 text-sm rounded-lg hover:bg-emerald-800 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-phone-line"></i> Call Now
            </a>
            <button onClick={handleScrollToContact} className="px-6 py-3 border-2 border-emerald-700 text-emerald-700 font-['Poppins'] font-600 text-sm rounded-lg hover:bg-emerald-700 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              Send a Message
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
